import React from "react";
import { HintSurfaceContext, Kbd } from "../../../components/shortcuts/Kbd";
import { benchGroupWork } from "../../../game/bench-work/workpiece";
import { CONSUMABLE_TYPES, ConsumableId } from "../../../game/Consumable";
import { TOOL_TYPES } from "../../../game/Tool";
import { toolMountRefusalKind } from "../../../game/tool-rack";
import { BenchAssemblyView } from "../../scenes/bench/BenchAssemblyView";
import { BenchDive } from "../../scenes/bench/BenchDive";
import { BenchGlueView } from "../../scenes/bench/BenchGlueView";
import { BenchTrayView } from "../../scenes/bench/BenchTrayView";
import { openBenchGroup } from "../../scenes/bench/benchStage";
import { useGame, useShellVersion, useShopState } from "../../useShell";
import { useBenchChrome } from "./useBenchDive";

/**
 * The key hints in a row along the bottom of the bench view, between
 * the two trays: what the keys do with the hands as they are. The stack answers only to
 * deliberate acts — taking a tool, a clamp, or the bottle down; starting
 * a cut — never to what the pointer happens to be over, so it holds
 * still while the hand moves across the bench. (Which is why F always
 * reads "flip": the piece itself shows which way it went.) The prose is
 * confined to refusals: the glue-up's, which appears only after stock
 * has been laid across the clamps, and the rail's, when the tool in
 * hand can't hang here and so can't work here — the bench top shows
 * everything else.
 */
/** "nail" or "screw": the one the drawing fastens with. */
function fastenerNoun(id: ConsumableId): string {
  return id === "screws" ? "screw" : "nail";
}

export const BenchKeyHints: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const gameState = useShopState();
  const chrome = useBenchChrome();
  const dive = game.entities.tryGetSingleton(BenchDive);
  const glue = game.entities.tryGetSingleton(BenchGlueView);
  const build =
    game.entities.tryGetSingleton(BenchAssemblyView)?.assemblyProgress() ??
    null;
  const run = openBenchGroup(game);
  if (!dive || dive.openBenchKey === null || !run) return null;
  // The plan folder covers this corner while it's up, and the keys it
  // hints at aren't the folder's
  if (dive.plansOpen) return null;
  // An opened tray fans across the bottom band, where this row sits:
  // the hints step aside while the hand is over one.
  if (game.entities.tryGetSingleton(BenchTrayView)?.openTray()) return null;

  const script = benchGroupWork(
    run.group.members,
    run.opened,
    gameState.progression,
  ).script;
  const held = dive.heldTool;
  const heldName = held ? TOOL_TYPES[held].name.toLowerCase() : null;
  // A tool off the rail hangs back up; one out of a tray goes back there.
  const heldFrom = dive.heldToolSource();
  const anyPieces = run.group.members.some(
    (member) =>
      member.machine.inputMaterials.length > 0 ||
      member.machine.outputMaterials.length > 0,
  );
  // Escape backs out one layer at a time: it lets go of whatever's in
  // hand, and with the hands empty it stands the player back up (the
  // step-back tag at the top does the same by click). The
  // put-back chip carries the old rail hint's test id, since it's the
  // same lesson moved.
  const putBack = (label: string): Hint => ({
    key: "Esc",
    label,
    testId: "bench-put-back-hint",
  });

  const hints: Hint[] = dive.holdingClamp
    ? [
        { key: "Click", label: "lay the clamp down" },
        putBack("put the clamp back"),
      ]
    : dive.holdingGlue
      ? [
          { key: "Drag", label: "spread glue on a seam" },
          putBack("put the glue away"),
        ]
      : held
        ? [
            ...(script?.kind === "pry"
              ? [{ key: "Hold + drag", label: "pull a nail" }]
              : build?.driverHeld
                ? [
                    build.fastenerId === "screws"
                      ? { key: "Tap", label: "drive a screw" }
                      : {
                          key: "Tap",
                          label:
                            "drive a nail, three blows, as the hammer comes back up",
                        },
                    {
                      key: "Hold + drag",
                      label: `pull a ${fastenerNoun(build.fastenerId)} out`,
                    },
                  ]
                : [
                    {
                      key: "Drag",
                      label:
                        script?.kind === "saw"
                          ? "saw the line"
                          : "work a piece",
                    },
                  ]),
            ...(sawsWithoutACut(held, script)
              ? [{ key: "R", label: "swing the angle" }]
              : []),
            ...(script?.kind === "pry"
              ? [{ key: "F", label: "flip the pallet" }]
              : []),
            putBack(
              heldFrom?.kind === "hook"
                ? `hang the ${heldName} up`
                : `put the ${heldName} back`,
            ),
          ]
        : [
            ...(anyPieces
              ? [
                  { key: "Drag", label: "move a piece" },
                  { key: "R", label: "turn" },
                  { key: "F", label: "flip" },
                ]
              : []),
            { key: "Esc", label: "step back", testId: "bench-step-back-hint" },
          ];

  const refusal =
    glue?.glueRefusal() ??
    (held && heldFrom && heldFrom.kind !== "hook"
      ? railRefusal(run.opened, held)
      : null);
  // The drawing's tool in hand with none of its fastener to drive: the
  // crossings go quiet, and this says where more come from
  const shortfall =
    build?.driverHeld && build.onHand === 0 && build.fasteners > 0
      ? build.fastenerId === "nails"
        ? "Out of nails. Pry some from a pallet, or buy a box at the store."
        : `Out of ${CONSUMABLE_TYPES[build.fastenerId].name.toLowerCase()}. The store sells them by the box.`
      : null;

  return (
    <HintSurfaceContext.Provider value="chrome">
      <div
        className={`pointer-events-none absolute bottom-5 left-1/2 z-30 flex max-w-[50vw] -translate-x-1/2 flex-row flex-wrap items-center justify-center gap-1.5 ${chrome.className}`}
        data-testid="bench-key-hints"
      >
        {hints.map(({ key, label, testId }) => (
          <span
            key={`${key}-${label}`}
            data-testid={testId}
            className="flex items-baseline gap-1.5 rounded bg-ink-black/60 px-2 py-1 font-condensed uppercase tracking-[0.12em] text-[0.62rem] text-paper-manila/70"
          >
            <Kbd>{key}</Kbd>
            {label}
          </span>
        ))}
        {refusal && (
          <span
            data-testid="bench-glue-refusal"
            className="rounded bg-ink-black/70 px-3 py-1 font-condensed uppercase tracking-[0.15em] text-[0.62rem] text-paper-manila"
          >
            {refusal}
          </span>
        )}
        {shortfall && (
          <span
            data-testid="bench-supply-refusal"
            className="rounded bg-ink-black/70 px-3 py-1 font-condensed uppercase tracking-[0.15em] text-[0.62rem] text-paper-manila"
          >
            {shortfall}
          </span>
        )}
      </div>
    </HintSurfaceContext.Provider>
  );
};

interface Hint {
  key: string;
  label: string;
  testId?: string;
}

/**
 * Why the tool in hand can't work at this bench: using a tool hangs it
 * on the rail (BenchDive.settleHeldTool), so a tool the rail won't take
 * offers nothing on the top, and the row says so instead of leaving
 * the hand to wonder.
 */
function railRefusal(
  opened: Parameters<typeof toolMountRefusalKind>[0],
  held: keyof typeof TOOL_TYPES,
): string | null {
  switch (toolMountRefusalKind(opened, held)) {
    case "full":
      return "The rail is full. Take a tool down to work with this one";
    case "incompatible":
      return `The ${TOOL_TYPES[held].name.toLowerCase()} doesn't hang on a bench`;
    case "working":
      return "The bench is busy";
    case null:
      return null;
  }
}

/** Whether R would swing the miter box right now: a saw in hand with no
 * cut marked yet (the stop locks once the line is on the board). */
function sawsWithoutACut(
  held: keyof typeof TOOL_TYPES,
  script: { kind: string; started?: boolean } | null,
): boolean {
  const saws = TOOL_TYPES[held].operations.some(
    (operation) => operation.interaction?.kind === "saw",
  );
  return saws && !(script?.kind === "saw" && script.started);
}
